/**
 * Bouton « + Ajouter un actif » de la fiche chantier et sa fenetre de saisie.
 *
 * Sorti de `pages/DistributorClientDetail.jsx` le 26/10 (lot L1, fichier > 500 lignes),
 * sans changement de comportement. Le client n'est pas envoye : le serveur le deduit du
 * chantier (`add-asset`), ce qui empeche de rattacher du materiel a un client devine.
 *
 * LOT L4 : le serveur interroge desormais l'appareil a cet ajout (meme regle que la
 * fiche client). L'ecran dit ce qu'il a repondu, garde la saisie sur un refus (numero
 * deja pris, trop de verifications), et n'envoie jamais deux fois : chaque envoi coute
 * un appel au fabricant, sur un quota partage avec la preproduction.
 */
import { useState } from 'react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { toast } from 'sonner';
import useAjoutActif from '@/features/clients/hooks/useAjoutActif';

const formulaireVide = () => ({ type: 'PAC', marque: '', modele: '', puissance_kw: '', identifiant_equipement: '', reference: '', date_installation: new Date().toISOString().slice(0, 10) });

export default function AjoutActifChantier({ refChantier, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(formulaireVide);
  // Le verrou d'envoi unique est celui de la fiche client (`useAjoutActif`), pas une copie.
  const { enCours: saving, ajouter } = useAjoutActif({
    api, toast, prefixe: 'Actif ajouté au chantier',
    url: `/distributeur/chantiers/${encodeURIComponent(refChantier)}/add-asset`,
  });
  const fermer = () => { if (!saving) setOpen(false); };
  const submit = async () => {
    if (!form.marque || !form.modele || !form.identifiant_equipement) { toast.error('Champs obligatoires manquants'); return; }
    if (await ajouter(null, form)) {
      setOpen(false);
      setForm(formulaireVide());
      onAdded?.();
    }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="mt-2" data-testid="add-asset-chantier-btn">
        + Ajouter un actif
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={fermer} data-testid="add-asset-modal">
          <div className="bg-white rounded-lg p-5 max-w-md w-full space-y-3 text-left" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-neutral-900">Ajouter un actif au chantier {refChantier}</h3>
            <div>
              <Label className="text-[11px] text-neutral-500">Type *</Label>
              <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="w-full h-9 border border-neutral-200 rounded-md px-2 text-[13px]" data-testid="add-asset-type">
                <option>PAC</option><option>PV</option><option>Batterie</option><option>Ballon</option><option>Radiateur</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[11px]">Marque *</Label><Input value={form.marque} onChange={(e) => setForm({...form, marque: e.target.value})} className="h-9" data-testid="add-asset-marque" /></div>
              <div><Label className="text-[11px]">Modèle *</Label><Input value={form.modele} onChange={(e) => setForm({...form, modele: e.target.value})} className="h-9" data-testid="add-asset-modele" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[11px]">Puissance (kW)</Label><Input type="number" step="0.1" value={form.puissance_kw} onChange={(e) => setForm({...form, puissance_kw: e.target.value})} className="h-9" data-testid="add-asset-puissance" /></div>
              <div><Label className="text-[11px]">Date pose</Label><Input type="date" value={form.date_installation} onChange={(e) => setForm({...form, date_installation: e.target.value})} className="h-9" /></div>
            </div>
            <div><Label className="text-[11px]">N° série *</Label><Input value={form.identifiant_equipement} onChange={(e) => setForm({...form, identifiant_equipement: e.target.value})} className="h-9" data-testid="add-asset-serial" /></div>
            <div><Label className="text-[11px]">Référence produit</Label><Input value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})} className="h-9" data-testid="add-asset-ref" /></div>
            <div className="flex justify-end gap-2 pt-2 border-t border-neutral-100">
              <Button size="sm" variant="ghost" onClick={fermer} disabled={saving}>Annuler</Button>
              <Button size="sm" onClick={submit} disabled={saving} data-testid="add-asset-submit">{saving ? 'Vérification de l’appareil…' : 'Ajouter'}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
